import { Component, OnInit, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { MessageService, ConfirmationService } from 'primeng/api';
import { FileUpload } from 'primeng/fileupload';
import { forkJoin } from 'rxjs';
import { PermissoesService } from 'src/services/permissoes.service';
import { EstacaoService } from 'src/services/estacao.service';
import { PessoaService } from 'src/services/pessoa.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DocumentoRepositorioService } from 'src/services/documento-repositorio.service';
import { ArquivosService } from 'src/services/arquivos.service';

interface DropdownOption {
  label: string;
  value: number;
}

const TODOS_CLIENTES_ID = -1;

interface ClienteAssociado {
  id: number;
  nome: string;
}

interface DocumentoRepositorio {
  id: number;
  descricao: string;
  tipo_documento: number;
  tipo_documento_descricao?: string;
  estacao_id?: number | null;
  cliente_id?: number | null;
  todos_clientes?: boolean;
  cliente_ids?: number[];
  clientes?: ClienteAssociado[];
  cliente?: ClienteAssociado | null;
  compartilhado?: boolean;
  vinculo_nome?: string | null;
  vinculo_nome_exibicao?: string;
  clientes_nomes?: string;
  quantidade_documentos?: number;
  [key: string]: unknown;
}

type DocumentoRepositorioPayload =
  | { descricao: string; tipo_documento: 0; estacao_id: number }
  | { descricao: string; tipo_documento: 1; todos_clientes: boolean; cliente_ids?: number[] };

@Component({
    selector: 'app-documento-repositorio',
    templateUrl: './documento-repositorio.component.html',
    styleUrls: ['./documento-repositorio.component.scss'],
    standalone: false
})
export class DocumentoRepositorioComponent implements OnInit, AfterViewInit {
  @ViewChild('tableWrapper', { static: false }) tableWrapper: ElementRef;
  @ViewChild('uploadArquivoFilho') uploadArquivoFilho?: FileUpload;
  scrollHeight: string = '400px';

  data: DocumentoRepositorio[] = [];
  loading: boolean = false;
  novoDocumentoModal: boolean = false;
  modalDocumentos: boolean = false;
  documentosVinculo: any[] = [];
  vinculoSelecionado: any = null;
  documentoPreview: any = null;
  modalPreview: boolean = false;
  novoArquivoModal: boolean = false;
  loadingDocumentos: boolean = false;
  salvandoDocumento: boolean = false;
  uploadingArquivo: boolean = false;
  uploadArquivoProgress: number = 0;
  editandoDocumentoId: number | null = null;

  descricaoNovoDocumento: string = '';
  tipoDocumento: number | null = null;
  estacaoSelecionadaParaUpload: number | null = null;
  clientesSelecionadosParaUpload: number[] = [];
  descricaoNovoArquivo: string = '';
  arquivoSelecionadoUpload: File | null = null;

  dropDownEstacoes: DropdownOption[] = [];
  dropDownClientes: DropdownOption[] = [];
  dropDownTipoDocumento: any[] = [
    { label: 'Estação', value: 0 },
    { label: 'Cliente', value: 1 }
  ];

  private clienteIdsDisponiveis: number[] = [];

  constructor(
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private documentoRepositorioService: DocumentoRepositorioService,
    private arquivosService: ArquivosService,
    private estacaoService: EstacaoService,
    private pessoaService: PessoaService,
    protected permissaoService: PermissoesService,
    private sanitizer: DomSanitizer,
  ) { }

  get todosClientesSelecionado(): boolean {
    return this.clientesSelecionadosParaUpload.includes(TODOS_CLIENTES_ID);
  }

  ngOnInit(): void {
    this.carregarDadosIniciais();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.calculateScrollHeight();
    }, 100);
  }

  calculateScrollHeight(): void {
    if (this.tableWrapper && this.tableWrapper.nativeElement) {
      const wrapperHeight = this.tableWrapper.nativeElement.offsetHeight;
      // Subtrai apenas altura do caption (~60px) e paginator (~60px), header já está dentro do scrollable
      const calculatedHeight = wrapperHeight - 120;
      this.scrollHeight = `${Math.max(calculatedHeight, 300)}px`;
    }
  }

  carregarDadosIniciais(): void {
    this.loading = true;

    forkJoin({
      documentos: this.documentoRepositorioService.index(),
      estacoes: this.estacaoService.lookup(),
      clientes: this.pessoaService.listEmpresas(),
    }).subscribe({
      next: ({ documentos, estacoes, clientes }) => {
        this.carregarDropdowns(estacoes, clientes);
        const lista = documentos?.data || documentos || [];
        this.data = this.normalizarDocumentos(Array.isArray(lista) ? lista : []);
        this.loading = false;
        setTimeout(() => this.calculateScrollHeight(), 100);
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Erro ao carregar documentos' });
        this.loading = false;
      }
    });
  }

  index(): void {
    this.loading = true;
    this.documentoRepositorioService.index().subscribe({
      next: (res) => {
        const lista = res?.data || res || [];
        this.data = this.normalizarDocumentos(Array.isArray(lista) ? lista : []);
        this.loading = false;
        setTimeout(() => this.calculateScrollHeight(), 100);
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Erro ao carregar documentos' });
        this.loading = false;
      }
    });
  }

  private carregarDropdowns(estacoesRes: any, clientesRes: any): void {
    const estacoes = estacoesRes?.data || estacoesRes || [];
    const clientes = clientesRes?.data || clientesRes || [];

    this.dropDownEstacoes = (Array.isArray(estacoes) ? estacoes : []).map((item: any) => ({
      label: item.descricao || `Estação ${item.id}`,
      value: Number(item.id),
    }));

    const opcoesClientes = (Array.isArray(clientes) ? clientes : []).map((item: any) => ({
      label: item.nome || item.razao_social || `Cliente ${item.id}`,
      value: Number(item.id)
    }));
    this.clienteIdsDisponiveis = opcoesClientes.map((item) => item.value);
    this.dropDownClientes = [
      { label: 'Todos os clientes', value: TODOS_CLIENTES_ID },
      ...opcoesClientes
    ];
  }

  abrirModalNovoDocumento(vinculo?: any): void {
    this.resetarFormularioDocumento();
    this.novoDocumentoModal = true;
  }

  editarDocumento(documento: DocumentoRepositorio): void {
    this.resetarFormularioDocumento();
    this.novoDocumentoModal = true;
    this.editandoDocumentoId = documento.id;
    this.descricaoNovoDocumento = documento.descricao || '';
    this.tipoDocumento = documento.tipo_documento == null ? null : Number(documento.tipo_documento);
    this.estacaoSelecionadaParaUpload = this.tipoDocumento === 0 && documento.estacao_id != null
      ? Number(documento.estacao_id)
      : null;

    if (this.tipoDocumento === 1) {
      if (documento.todos_clientes === true) {
        this.clientesSelecionadosParaUpload = [TODOS_CLIENTES_ID];
        return;
      }
      const clienteIds = this.resolverClienteIds(documento);
      this.adicionarOpcoesDeClientesAssociados(documento, clienteIds);
      this.clientesSelecionadosParaUpload = clienteIds;
    }
  }

  fecharModalNovoDocumento(): void {
    this.novoDocumentoModal = false;
    this.resetarFormularioDocumento();
  }

  onTipoDocumentoChange(): void {
    this.estacaoSelecionadaParaUpload = null;
    this.clientesSelecionadosParaUpload = [];
  }

  onClientesChange(event: { value?: number[]; itemValue?: number | DropdownOption }): void {
    const selecionados = this.normalizarSelecaoClientes(event.value ?? this.clientesSelecionadosParaUpload);
    const itemValue = typeof event.itemValue === 'object'
      ? Number(event.itemValue?.value)
      : Number(event.itemValue);

    if (itemValue === TODOS_CLIENTES_ID && selecionados.includes(TODOS_CLIENTES_ID)) {
      this.clientesSelecionadosParaUpload = [TODOS_CLIENTES_ID];
      return;
    }

    this.clientesSelecionadosParaUpload = itemValue !== TODOS_CLIENTES_ID
      ? selecionados.filter((id) => id !== TODOS_CLIENTES_ID)
      : selecionados;
  }

  limparClientesSelecionados(): void {
    this.clientesSelecionadosParaUpload = [];
  }

  salvarNovoDocumento(): void {
    if (this.tipoDocumento === null || this.tipoDocumento === undefined) {
      this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Selecione o tipo de documento' });
      return;
    }

    if (this.tipoDocumento === 0 && !this.estacaoSelecionadaParaUpload) {
      this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Selecione uma estação' });
      return;
    }

    if (this.tipoDocumento === 1
      && (!Array.isArray(this.clientesSelecionadosParaUpload) || this.clientesSelecionadosParaUpload.length === 0)) {
      this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Selecione ao menos um cliente' });
      return;
    }

    if (!this.descricaoNovoDocumento?.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Informe a descrição' });
      return;
    }

    this.salvandoDocumento = true;
    const payload: DocumentoRepositorioPayload = this.tipoDocumento === 0
      ? {
          descricao: this.descricaoNovoDocumento.trim(),
          tipo_documento: 0,
          estacao_id: Number(this.estacaoSelecionadaParaUpload)
        }
      : {
          descricao: this.descricaoNovoDocumento.trim(),
          tipo_documento: 1,
          todos_clientes: this.todosClientesSelecionado,
          ...(this.todosClientesSelecionado
            ? {}
            : { cliente_ids: this.normalizarIds(this.clientesSelecionadosParaUpload) })
        };

    const request$ = this.editandoDocumentoId
      ? this.documentoRepositorioService.update(payload, this.editandoDocumentoId)
      : this.documentoRepositorioService.store(payload);

    request$.subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: this.editandoDocumentoId ? 'Documento atualizado com sucesso' : 'Documento salvo com sucesso'
        });
        this.salvandoDocumento = false;
        this.fecharModalNovoDocumento();
        this.index();
      },
      error: (err) => {
        const errorMsg = this.extrairMensagemErro(err, 'Erro ao salvar documento');
        this.messageService.add({ severity: 'error', summary: 'Erro', detail: errorMsg });
        this.salvandoDocumento = false;
      }
    });
  }

  formatarVinculo(documento: DocumentoRepositorio): string {
    if (Number(documento.tipo_documento) !== 1) {
      return documento.vinculo_nome || 'N/A';
    }

    if (documento.todos_clientes === true) {
      return 'Todos os clientes';
    }

    const nomes = this.obterNomesClientes(documento);
    if (nomes.length === 0) {
      return documento.vinculo_nome || 'N/A';
    }

    const total = Math.max(this.resolverClienteIds(documento).length, nomes.length);
    const compartilhado = documento.compartilhado === true || total > 1;
    if (!compartilhado || total === 1) {
      return nomes[0];
    }

    const adicionais = total - 1;
    return `${nomes[0]} + ${adicionais} ${adicionais === 1 ? 'cliente' : 'clientes'}`;
  }

  obterTooltipVinculo(documento: DocumentoRepositorio): string {
    if (Number(documento.tipo_documento) === 1 && documento.todos_clientes === true) {
      return 'Inclui automaticamente todos os clientes atuais e futuros';
    }
    return Number(documento.tipo_documento) === 1
      ? this.obterNomesClientes(documento).join(', ')
      : (documento.vinculo_nome || 'N/A');
  }

  private resetarFormularioDocumento(): void {
    this.editandoDocumentoId = null;
    this.descricaoNovoDocumento = '';
    this.tipoDocumento = null;
    this.estacaoSelecionadaParaUpload = null;
    this.clientesSelecionadosParaUpload = [];
    this.dropDownClientes = this.dropDownClientes.filter((opcao) =>
      opcao.value === TODOS_CLIENTES_ID || this.clienteIdsDisponiveis.includes(opcao.value)
    );
  }

  private normalizarDocumentos(documentos: any[]): DocumentoRepositorio[] {
    return documentos.map((item: DocumentoRepositorio) => ({
      ...item,
      tipo_documento: Number(item.tipo_documento),
      vinculo_nome_exibicao: this.formatarVinculo(item),
      clientes_nomes: this.obterNomesClientes(item).join(' ')
    }));
  }

  private resolverClienteIds(documento: DocumentoRepositorio): number[] {
    if (Object.prototype.hasOwnProperty.call(documento, 'cliente_ids') && Array.isArray(documento.cliente_ids)) {
      return this.normalizarIds(documento.cliente_ids);
    }

    const clienteLegadoId = documento.cliente_id ?? documento.cliente?.id;
    return clienteLegadoId == null ? [] : this.normalizarIds([clienteLegadoId]);
  }

  private normalizarIds(ids: unknown[]): number[] {
    return Array.from(new Set(ids
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0)));
  }

  private normalizarSelecaoClientes(ids: unknown[]): number[] {
    return Array.from(new Set(ids
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && (id > 0 || id === TODOS_CLIENTES_ID))));
  }

  private adicionarOpcoesDeClientesAssociados(documento: DocumentoRepositorio, clienteIds: number[]): void {
    const nomesPorId = new Map<number, string>();
    (Array.isArray(documento.clientes) ? documento.clientes : []).forEach((cliente) => {
      nomesPorId.set(Number(cliente.id), cliente.nome || `Cliente ${cliente.id}`);
    });
    if (documento.cliente?.id != null) {
      nomesPorId.set(Number(documento.cliente.id), documento.cliente.nome || `Cliente ${documento.cliente.id}`);
    }

    const idsExistentes = new Set(this.dropDownClientes.map((opcao) => opcao.value));
    const opcoesAusentes = clienteIds
      .filter((id) => !idsExistentes.has(id))
      .map((id) => ({ label: nomesPorId.get(id) || `Cliente ${id}`, value: id }));

    if (opcoesAusentes.length > 0) {
      this.dropDownClientes = [...this.dropDownClientes, ...opcoesAusentes];
    }
  }

  private obterNomesClientes(documento: DocumentoRepositorio): string[] {
    const nomesPorId = new Map<number, string>();
    (Array.isArray(documento.clientes) ? documento.clientes : []).forEach((cliente) => {
      nomesPorId.set(Number(cliente.id), cliente.nome || `Cliente ${cliente.id}`);
    });
    if (documento.cliente?.id != null) {
      nomesPorId.set(Number(documento.cliente.id), documento.cliente.nome || `Cliente ${documento.cliente.id}`);
    }
    const ids = this.resolverClienteIds(documento);
    if (ids.length > 0) {
      return ids.map((id) =>
        nomesPorId.get(id)
        || this.dropDownClientes.find((opcao) => opcao.value === id)?.label
        || `Cliente ${id}`
      );
    }

    if (nomesPorId.size > 0) {
      return Array.from(nomesPorId.values());
    }

    return documento.vinculo_nome ? [documento.vinculo_nome] : [];
  }

  private extrairMensagemErro(err: any, fallback: string): string {
    const body = err?.error;
    if (typeof body?.message === 'string' && body.message.trim()) {
      return body.message;
    }

    if (body?.errors && typeof body.errors === 'object') {
      for (const value of Object.values(body.errors)) {
        if (Array.isArray(value)) {
          const mensagem = value.find((item) => typeof item === 'string' && item.trim());
          if (mensagem) return mensagem;
        }
        if (typeof value === 'string' && value.trim()) {
          return value;
        }
      }
    }

    return fallback;
  }

  getFileIcon(item: any): string {
    const nome = item.nome || item.nome_arquivo || '';
    const extensao = nome.split('.').pop()?.toLowerCase() || '';
    const iconMap: { [key: string]: string } = {
      'pdf': 'pi-file-pdf',
      'doc': 'pi-file-word',
      'docx': 'pi-file-word',
      'xls': 'pi-file-excel',
      'xlsx': 'pi-file-excel',
      'png': 'pi-image',
      'jpg': 'pi-image',
      'jpeg': 'pi-image',
      'gif': 'pi-image',
    };
    return iconMap[extensao] || 'pi-file';
  }

  abrirModalDocumentos(documento: any): void {
    this.vinculoSelecionado = documento;
    this.documentosVinculo = [];
    this.modalDocumentos = true;
    this.carregarArquivosRelacionados(documento.id);
  }

  carregarArquivosRelacionados(documentoId: number): void {
    this.loadingDocumentos = true;
    this.documentoRepositorioService.getArquivos(documentoId).subscribe({
      next: (res) => {
        this.documentosVinculo = res.data || res || [];
        this.loadingDocumentos = false;
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Erro ao carregar documentos' });
        this.loadingDocumentos = false;
      }
    });
  }

  abrirModalNovoArquivo(documento?: any): void {
    this.vinculoSelecionado = documento || this.vinculoSelecionado;
    this.descricaoNovoArquivo = '';
    this.arquivoSelecionadoUpload = null;
    this.novoArquivoModal = true;

    if (this.uploadArquivoFilho) {
      this.uploadArquivoFilho.clear();
    }
  }

  fecharModalNovoArquivo(): void {
    this.novoArquivoModal = false;
    this.descricaoNovoArquivo = '';
    this.arquivoSelecionadoUpload = null;
    this.uploadArquivoProgress = 0;

    if (this.uploadArquivoFilho) {
      this.uploadArquivoFilho.clear();
    }
  }

  onFileSelect(event: any): void {
    this.arquivoSelecionadoUpload = event.currentFiles?.[0] || null;
  }

  onFileRemove(): void {
    this.arquivoSelecionadoUpload = null;
  }

  async salvarNovoArquivo(): Promise<void> {
    if (!this.vinculoSelecionado?.id) {
      this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Selecione um documento' });
      return;
    }

    if (!this.arquivoSelecionadoUpload) {
      this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Selecione um arquivo' });
      return;
    }

    this.uploadingArquivo = true;
    this.uploadArquivoProgress = 0;

    try {
      await this.arquivosService.upload(
        this.arquivoSelecionadoUpload,
        this.descricaoNovoArquivo || '',
        this.vinculoSelecionado.tipo_documento,
        this.vinculoSelecionado.estacao_id ?? undefined,
        this.vinculoSelecionado.cliente_id ?? undefined,
        this.vinculoSelecionado.id,
        (progress) => this.uploadArquivoProgress = progress
      );

      this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Arquivo enviado com sucesso' });
      this.fecharModalNovoArquivo();
      this.carregarArquivosRelacionados(this.vinculoSelecionado.id);
      this.index();
    } catch (err: any) {
      const errorMsg = err?.error?.message || err?.message || 'Erro ao enviar arquivo';
      this.messageService.add({ severity: 'error', summary: 'Erro', detail: errorMsg });
    } finally {
      this.uploadingArquivo = false;
    }
  }

  podeVisualizar(documento: any): boolean {
    const tipoArquivo = documento.tipo_arquivo?.toLowerCase() || '';
    const nome = documento.nome?.toLowerCase() || '';

    if (tipoArquivo.includes('pdf') || 
        tipoArquivo.includes('image/') ||
        tipoArquivo.includes('msword') || 
        tipoArquivo.includes('wordprocessingml') ||
        tipoArquivo.includes('spreadsheetml') ||
        tipoArquivo.includes('ms-excel')) {
      return true;
    }
    
    const extensao = nome.split('.').pop()?.toLowerCase() || '';
    return ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'].includes(extensao);
  }

  abrirPreview(documento: any): void {
    this.documentoPreview = documento;
    this.modalPreview = true;
  }

  fecharPreview(): void {
    this.modalPreview = false;
    this.documentoPreview = null;
  }

  getPreviewUrl(documento: any): SafeResourceUrl {
    const url = this.resolveDocumentoUrl(documento) || '';
    const nome = documento.nome?.toLowerCase() || '';
    const extensao = nome.split('.').pop()?.toLowerCase() || '';
    
    let previewUrl = url;
    if (['doc', 'docx', 'xls', 'xlsx'].includes(extensao)) {
      previewUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
    }
    
    return this.sanitizer.bypassSecurityTrustResourceUrl(previewUrl);
  }

  downloadDocumento(documento: any): void {
    if (!documento) return;
    
    const url = this.resolveDocumentoUrl(documento);
    if (url) {
      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = documento.nome || 'documento';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
      } catch (err) {
        window.open(url, '_blank');
      }
    }
  }

  private resolveDocumentoUrl(documento: any): string | null {
    const url = documento?.url;
    return typeof url === 'string' && url.trim() !== '' ? url : null;
  }

  formatarTamanho(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

}
